import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, X } from 'lucide-react';
import { RECURRING_TYPES, currentMonth } from '@/lib/payments';

const API = `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '')}/api`;

/**
 * Crear o editar un plan mensual (viático / canasta).
 *  - plan: si viene, se edita (monto y notas)
 *  - initialPlayer {id, name}: jugador ya elegido (desde la ficha)
 *  - initialType: tipo preseleccionado
 */
const PlanDialog = ({ open, onOpenChange, onSaved, plan = null, initialPlayer = null, initialType = 'viaticos' }) => {
  const isEdit = !!plan;
  const [player, setPlayer] = useState(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [type, setType] = useState(initialType);
  const [amount, setAmount] = useState('');
  const [startMonth, setStartMonth] = useState(currentMonth());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (plan) {
      setPlayer({ id: plan.player_id, name: plan.player_name });
      setType(plan.type);
      setAmount(String(plan.amount ?? ''));
      setStartMonth(plan.start_month || currentMonth());
      setNotes(plan.notes || '');
    } else {
      setPlayer(initialPlayer);
      setType(initialType);
      setAmount('');
      setStartMonth(currentMonth());
      setNotes('');
    }
    setSearch('');
    setResults([]);
  }, [open, plan, initialPlayer?.id, initialPlayer?.name, initialType]);

  useEffect(() => {
    if (!open || isEdit || player || search.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/players`, { params: { search: search.trim(), limit: 8 } });
        setResults((res.data?.data || []).filter((p) => p.status !== 'inactive'));
      } catch (e) {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, open, isEdit, player]);

  const handleSave = async () => {
    const value = parseFloat(amount);
    if (!player) return toast.error('Elegí un jugador');
    if (!value || value <= 0) return toast.error('Ingresá un monto mensual mayor a 0');
    try {
      setSaving(true);
      if (isEdit) {
        await axios.put(`${API}/recurring/plans/${plan.id}`, { amount: value, notes });
        toast.success('Plan actualizado');
      } else {
        await axios.post(`${API}/recurring/plans`, {
          player_id: player.id, type, amount: value, start_month: startMonth || null, notes: notes || null,
        });
        toast.success('Pago mensual configurado');
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar pago mensual' : 'Nuevo pago mensual'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'El nuevo monto rige para las cuotas todavía no pagadas del mes actual en adelante.'
              : 'Se genera una cuota por mes, automáticamente, hasta que el jugador quede inactivo o se finalice el plan.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Jugador *</Label>
            {player ? (
              <div className="mt-1 flex items-center justify-between rounded-md border bg-gray-50 px-3 py-2 text-sm">
                <span className="font-medium">{player.name}</span>
                {!isEdit && !initialPlayer && (
                  <button type="button" onClick={() => setPlayer(null)} className="text-gray-400 hover:text-gray-700" aria-label="Cambiar jugador">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar jugador por nombre..."
                  className="pl-9"
                  data-testid="plan-player-search"
                />
                {results.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-white shadow-md">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => setPlayer({ id: p.id, name: `${p.first_name} ${p.last_name}` })}
                        data-testid={`plan-player-option-${p.id}`}
                      >
                        {p.first_name} {p.last_name}
                        <span className="ml-2 text-xs text-gray-500">{p.origin_club || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Concepto *</Label>
              <Select value={type} onValueChange={setType} disabled={isEdit}>
                <SelectTrigger className="mt-1" data-testid="plan-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Monto mensual ($) *</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="mt-1" placeholder="0" data-testid="plan-amount" />
            </div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Primer mes que se paga</Label>
              <Input type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className="mt-1" data-testid="plan-start" />
            </div>
          )}
          <div>
            <Label className="text-xs">Notas (opcional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-600 hover:bg-brand-700" data-testid="plan-save">
            {saving ? 'Guardando...' : isEdit ? 'Guardar' : 'Configurar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanDialog;
